import { computed, type ComputedRef } from 'vue';
import { usePlannerStore } from '@/views/3d-planner/store/plannerStore';
import { usePlannerSceneViewModel } from '@/views/3d-planner/store/plannerSceneViewModel';
import type { PlannerMode } from '@/views/3d-planner/store/plannerState';
import { useTemplateSyncStore } from '@/views/3d-planner/template/templateSyncStore';
import { planBaselineTemplate, planIsOutOfDate } from '@/views/3d-planner/template/planStaleness';
import { useUserStore } from '@/stores/userStore';
import type { PlanAct } from '@/domain/plan/planAct';

const LABELS: Record<PlanAct, string> = {
    approve: 'Approve & Generate Plan',
    propose: 'Propose Plan',
    none: '',
};

/**
 * What a waiting proposal means to the caller looking at it.
 *
 * A preparer sees a disabled button here - they proposed the plan and cannot approve it - so
 * without this they are given no reason for it.
 */
const PROPOSAL_HINTS: Record<PlanAct, string | null> = {
    approve: 'This plan was proposed and is waiting for your approval.',
    propose: 'This plan was proposed and is waiting for the surgeon to approve it.',
    none: null,
};

/**
 * Why the button cannot be used.
 *
 * Mutually exclusive, and each names a different blocker - so which one applies is a decision
 * about what to say, which is this ViewModel's, not the View's.
 */
const TOOLTIPS = {
    noAct: 'You do not have permission to create a plan for this case.',
    incompleteChanges: 'There are incomplete changes.',
    nothingToApprove: 'There are no changes to approve.',
} as const;

type ButtonSituation = {
    act: PlanAct;
    plannerMode: PlannerMode;
    valid: boolean;

    /** An edit is still on its way to the server, so there is nothing settled to plan from. */
    unsavedUpdate: boolean;

    /** The plan no longer reflects the template, or there is no plan to reflect it. */
    outOfDate: boolean;

    proposalWaiting: boolean;
};

function isButtonEnabled(situation: ButtonSituation): boolean {
    const { act, plannerMode, valid, unsavedUpdate, outOfDate, proposalWaiting } = situation;

    if (plannerMode === 'disabled' || act === 'none' || !valid || unsavedUpdate) {
        return false;
    }

    // A surgeon may approve a proposal that nothing has changed since - that is the whole point of
    // it having been proposed.
    return outOfDate || (proposalWaiting && act === 'approve');
}

/** The precedence chain behind the button's tooltip, or null when the button is live. */
function tooltipFor(situation: ButtonSituation): string | null {
    if (situation.act === 'none') {
        return TOOLTIPS.noAct;
    }

    if (!situation.valid) {
        return TOOLTIPS.incompleteChanges;
    }

    if (!situation.outOfDate && !situation.proposalWaiting) {
        return TOOLTIPS.nothingToApprove;
    }

    return null;
}

/**
 * The view model behind the approve/propose button.
 *
 * `ApprovePlan.vue` sits under `src/views/**`, where lint-staged escalates the thin-view rules to
 * commit-blocking errors, so this logic cannot live in the component.
 */
export function useApprovePlanViewModel(valid: () => boolean) {
    const store = usePlannerStore();
    const scene = usePlannerSceneViewModel();
    const sync = useTemplateSyncStore();
    const user = useUserStore();

    const act: ComputedRef<PlanAct> = computed(() => user.planAct);

    /** The plan this case is measured against: the user plan's template, else the Formus one's. */
    const baseline = computed(() =>
        planBaselineTemplate(
            store.case?.userPlanTemplate ?? null,
            store.case?.autoTemplate ?? null,
        ),
    );
    const hasPlan = computed(() => baseline.value !== null);
    const outOfDate = computed(() => planIsOutOfDate(store.template, baseline.value));

    /** A proposed plan is still waiting on a surgeon, so approving it remains available. */
    const proposalAwaitingApproval = computed(
        () => store.case?.manualPlanApprovalState === 'proposed',
    );

    /** Everything the button's two decisions read, assembled once so they cannot disagree. */
    const situation = computed<ButtonSituation>(() => ({
        act: act.value,
        plannerMode: scene.plannerMode,
        valid: valid(),
        unsavedUpdate: sync.hasUpdate,
        outOfDate: outOfDate.value,
        proposalWaiting: proposalAwaitingApproval.value,
    }));

    return {
        act,
        label: computed(() => LABELS[act.value]),
        hasPlan,
        outOfDate,
        showChangesHint: computed(() => hasPlan.value && outOfDate.value),
        proposalHint: computed(() =>
            proposalAwaitingApproval.value ? PROPOSAL_HINTS[act.value] : null,
        ),
        isSaving: computed(() => sync.isSaving),
        tooltip: computed(() => tooltipFor(situation.value)),
        buttonDisabled: computed(() => !isButtonEnabled(situation.value)),
        submit: () => store.approvePlan(),
    };
}
